// ─────────────────────────────────────────────────────────────────────────────
// VoiceEngineToggle — the SEPARATE voice-engine axis control (Phase 4 D).
//
// Surfaces the manual "force Deepgram" override for the voice engine. Local Qwen is
// PRIMARY and default-on (voice routes local the moment Flask restarts, no UI needed);
// this control only lets the user deliberately switch to the Deepgram cloud voice and
// back. It is NOT the F1/D4 reasoning routing_mode — it models that control's
// interaction/depth language (segmented, inset well, pressed-in active) on a SEPARATE
// axis, in the REXY_CONSOLE design idiom of the audio surface it lives in.
//
// Honest state: reflects the ACTUAL backend mode from GET /api/voice/engine on mount and
// the effective mode returned by POST on change — never an assumed state. `localHealthy`
// is shown as a status dot; it does NOT gate anything (auto-fallback is a backend concern
// that fires only on true unreachability).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../lib/rexyConsole';
import {
  getVoiceEngine, setVoiceEngine, VoiceEngineMode, VoiceEngineStatus,
} from '../api/voiceEngine';

const MONO = REXY_FONT;

const OPTIONS: { mode: VoiceEngineMode; label: string; tip: string }[] = [
  { mode: 'local',    label: 'LOCAL',    tip: 'Local Qwen TTS — PRIMARY (default). Deepgram is used only if the DGX voice service is unreachable.' },
  { mode: 'deepgram', label: 'DEEPGRAM', tip: 'Force the Deepgram cloud voice — a manual override of the local-primary default.' },
];

export default function VoiceEngineToggle() {
  const [status, setStatus] = useState<VoiceEngineStatus | null>(null);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState(false);

  // Reflect the live backend mode on mount. Graceful on failure (endpoint not up yet /
  // pre-restart Flask): show UNKNOWN, never crash the card.
  useEffect(() => {
    let alive = true;
    getVoiceEngine()
      .then((s) => { if (alive) { setStatus(s); setErr(false); } })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, []);

  const select = (mode: VoiceEngineMode) => {
    if (pending || !status || status.mode === mode) return;
    setPending(true);
    setVoiceEngine(mode)
      .then((s) => { setStatus(s); setErr(false); })
      .catch(() => setErr(true))
      .finally(() => setPending(false));
  };

  const mode = status?.mode ?? null;
  const healthy = status?.localHealthy ?? false;
  const disabled = pending || !status;

  return (
    <div style={styles.block}>
      <div style={styles.rowTop}>
        <span style={styles.label}>ENGINE · TTS</span>
        <span
          style={{
            ...styles.statusDot,
            color: err ? REXY_CONSOLE.warning : healthy ? REXY_CONSOLE.primary : REXY_CONSOLE.dimText,
          }}
        >
          {err ? '○ UNKNOWN' : healthy ? '● LOCAL UP' : '○ LOCAL DOWN'}
        </span>
      </div>

      {/* Segmented control — F1/D4 pattern (inset well container + pressed-in active
          segment + top-edge highlight), rendered in the console (cyan/gold) idiom. */}
      <div style={styles.segWrap} onClick={(e) => e.stopPropagation()}>
        {OPTIONS.map((opt) => {
          const active = mode === opt.mode;
          const isLocal = opt.mode === 'local';
          const accent = isLocal ? REXY_CONSOLE.primary : REXY_CONSOLE.gold;
          const activeBg = isLocal ? REXY_CONSOLE.badgeOkBg : REXY_CONSOLE.badgeWrnBg;
          return (
            <button
              key={opt.mode}
              onClick={() => select(opt.mode)}
              title={opt.tip}
              aria-pressed={active}
              disabled={disabled}
              style={{
                ...styles.seg,
                color: active ? accent : REXY_CONSOLE.dimText,
                background: active ? activeBg : 'transparent',
                boxShadow: active
                  ? 'inset 0 1px 2px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.05)'
                  : 'none',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled && !active ? 0.6 : 1,
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      <div style={styles.sub}>
        {mode === 'deepgram'
          ? 'MANUAL OVERRIDE · CLOUD'
          : mode === 'local'
            ? (healthy ? 'PRIMARY · LOCAL QWEN' : 'LOCAL PRIMARY · FALLBACK IF UNREACHABLE')
            : '— · READING STATE'}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  block: { marginBottom: 12 },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  label: { fontFamily: MONO, fontSize: 11, letterSpacing: 1.5, color: REXY_CONSOLE.primary },
  statusDot: { fontFamily: MONO, fontSize: 9, letterSpacing: 1 },
  segWrap: {
    display: 'flex',
    gap: 3,
    padding: 3,
    borderRadius: 999,
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.panel,
    // the F1/D4 "well": inset recess + 1px top-edge highlight — dimensional, not flat
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04)',
  },
  seg: {
    flex: 1,
    fontFamily: MONO,
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    padding: '5px 6px',
    borderRadius: 999,
    border: 'none',
    transition: 'all 0.15s ease',
  },
  sub: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.dimText, marginTop: 6 },
};
